import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PlaceAutocomplete - Searchable dropdown for customer places
 * 
 * Features:
 * - Type at least 2 characters to see suggestions
 * - Places shown in alphabetical order
 * - Option to add new place if not in list
 * - Keyboard navigation support
 * - onEnterToNext callback for form navigation
 */
export default function PlaceAutocomplete({ value, onChange, placeholder = "Type 2+ chars to search...", onEnterToNext }) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Fetch suggestions when input changes (min 2 chars)
  useEffect(() => {
    if (inputValue.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/places", { params: { q: inputValue, limit: 15 } });
        setSuggestions(data.items || []);
        setShowDropdown(true);
        setHighlightIndex(-1);
      } catch (e) {
        console.error("Failed to fetch places:", e);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(debounce);
  }, [inputValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback((place) => {
    setInputValue(place.name);
    onChange(place.name);
    setShowDropdown(false);
  }, [onChange]);

  const handleAddNew = useCallback(async () => {
    const name = inputValue.trim();
    if (!name || name.length < 2) {
      toast.error("Place name must be at least 2 characters");
      return;
    }
    try {
      const { data } = await api.post("/places", { name });
      toast.success(`Place "${name}" added`);
      setInputValue(data.place.name);
      onChange(data.place.name);
      setShowDropdown(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add place");
    }
  }, [inputValue, onChange]);

  const handleKeyDown = useCallback((e) => {
    // If dropdown is showing, handle arrow navigation
    if (showDropdown && suggestions.length > 0) {
      const totalItems = suggestions.length + (inputValue.length >= 2 ? 1 : 0);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          return;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          return;
        case "Enter":
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
            handleSelect(suggestions[highlightIndex]);
          } else if (highlightIndex === suggestions.length && inputValue.length >= 2) {
            handleAddNew();
          } else {
            // No selection highlighted - close dropdown and move to next field
            setShowDropdown(false);
            onChange(inputValue);
            onEnterToNext?.();
          }
          return;
        case "Escape":
          setShowDropdown(false);
          return;
        default:
          break;
      }
    }
    
    // If dropdown is NOT showing or empty, Enter moves to next field
    if (e.key === "Enter") {
      e.preventDefault();
      onChange(inputValue);
      onEnterToNext?.();
    }
  }, [showDropdown, suggestions, highlightIndex, inputValue, handleSelect, handleAddNew, onChange, onEnterToNext]);

  // Check if current input matches any existing place
  const exactMatch = suggestions.some((p) => p.name.toLowerCase() === inputValue.toLowerCase());

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-8"
          data-testid="place-autocomplete-input"
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
          data-testid="place-dropdown"
        >
          {suggestions.length === 0 && inputValue.length >= 2 && !loading && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No places found matching &ldquo;{inputValue}&rdquo;
            </div>
          )}

          {suggestions.map((place, idx) => (
            <button
              key={place.id}
              type="button"
              onClick={() => handleSelect(place)}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 ${
                highlightIndex === idx ? "bg-gray-100" : ""
              }`}
              data-testid={`place-option-${idx}`}
            >
              <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span>{place.name}</span>
            </button>
          ))}

          {inputValue.length >= 2 && !exactMatch && (
            <button
              type="button"
              onClick={handleAddNew}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 border-t border-gray-100 text-emerald-700 hover:bg-emerald-50 ${
                highlightIndex === suggestions.length ? "bg-emerald-50" : ""
              }`}
              data-testid="place-add-new-btn"
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span>Add new place: <strong>&ldquo;{inputValue}&rdquo;</strong></span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
